import { Blac, Cubit } from "blac-next";
import { Step } from "src/ui/components/StepList/StepList";
import { tracker } from "src/state/state";
import expectationManagementSteps from "src/constants/expectationManagementSteps";
import UserPreferencesCubit, {
  UserPreferenceKeys
} from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import { addContextToExpectationManagementStep } from "src/lib/addContextToExpectationManagementStep";
import { CaourselStepChangeDetails } from "molecule/carousel/CarouselCubit";
import { ExperienceGroup } from "src/constants/experienceGroups";

export type ExpectationsManagementScreenState = {
  steps: Step[];
};

/**
 * Whose timeline copy to show. SHBP members who are eligible for meds get their own
 * milestones — medication and lifestyle support start together. Every other
 * combination falls back to the experience group timeline.
 */
export type ExpectationsManagementAudience = {
  userExperienceGroup: string | undefined;
  isShbpMember: boolean;
};

const SHBP_STEP_CONTEXT = "SHBP";

export class ExpectationsManagementScreenBloc extends Cubit<ExpectationsManagementScreenState> {
  constructor() {
    super({
      steps: []
    });
  }

  configure = (
    options: ExpectationsManagementAudience & {
      callbacks: Parameters<
        ExpectationsManagementScreenBloc["setCallbacks"]
      >[0];
    }
  ) => {
    this.setAudience(options);
    this.setCallbacks(options.callbacks);
    this.trackInitialView();
  };

  trackedInitial = false;
  private trackInitialView() {
    const experienceGroup = this.userExperienceGroup;

    if (!experienceGroup) {
      return;
    }

    if (!this.trackedInitial) {
      tracker.track("Expectation Management Screen Viewed", {
        data: { userExperienceGroup: experienceGroup }
      });
      this.trackedInitial = true;
    }
  }

  callbacks: { onDismiss: () => void } | null = null;
  setCallbacks = (callbacks: { onDismiss: () => void }) => {
    this.callbacks = callbacks;
  };

  userExperienceGroup: string | undefined;
  isShbpMember = false;
  setAudience = ({
    userExperienceGroup,
    isShbpMember
  }: ExpectationsManagementAudience) => {
    if (
      this.userExperienceGroup === userExperienceGroup &&
      this.isShbpMember === isShbpMember
    ) {
      return;
    }

    this.userExperienceGroup = userExperienceGroup;
    this.isShbpMember = isShbpMember;

    if (!userExperienceGroup) {
      this.patch({ steps: [] });
      return;
    }

    const steps = addContextToExpectationManagementStep(
      expectationManagementSteps,
      this.showsShbpTimeline ? SHBP_STEP_CONTEXT : userExperienceGroup
    );

    this.patch({ steps });
  };

  private get showsShbpTimeline(): boolean {
    return (
      this.isShbpMember &&
      this.userExperienceGroup === ExperienceGroup.HWJ_ELIGIBLE_FOR_MEDS
    );
  }

  handleDismissScreen = (triggerName: string) => {
    this.callbacks?.onDismiss();
    const userPreferences = Blac.getBloc(UserPreferencesCubit);
    void userPreferences.updateUserPreferences({
      [UserPreferenceKeys.showManagementExpectation]: false
    });
    tracker.track("Expectation Management Screen Dismissed", {
      data: { trigger: triggerName }
    });
  };

  handleStepChange = (index: number, details: CaourselStepChangeDetails) => {
    tracker.track(`Expectation Management Step Changed`, {
      data: {
        stepIndex: index,
        stepName: details.stepName,
        carouselId: details.carouselId,
        carouselLabel: details.carouselLabel
      }
    });
  };
}
